import {
  FC,
  ReactNode,
  useState,
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
} from 'react';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import { TabValue } from './types';

interface WbTabsContextValue {
  value: TabValue;
  onValueChange: (value: TabValue) => void;
  iconBreakpoint: Breakpoint;
  instanceId: string;
  getPanelId: (value: TabValue) => string;
  getTabId: (value: TabValue) => string;
}

const WbTabsInternalContext = createContext<WbTabsContextValue | undefined>(
  undefined,
);

export const useWbTabsContext = (): WbTabsContextValue => {
  const context = useContext(WbTabsInternalContext);
  if (!context) {
    throw new Error('useWbTabsContext must be used within WbTabsContext');
  }
  return context;
};

type WbTabsContextProps = {
  children: ReactNode;
  defaultValue?: TabValue;
  value?: TabValue;
  onValueChange?: (value: TabValue) => void;
  iconBreakpoint?: Breakpoint;
} & (
  | {
      defaultValue?: never;
      value: TabValue;
      onValueChange: (value: TabValue) => void;
    }
  | {
      defaultValue: TabValue;
      value?: never;
      onValueChange?: never;
    }
);

export const WbTabsContext: FC<WbTabsContextProps> = ({
  children,
  defaultValue,
  value: controlledValue,
  onValueChange,
  iconBreakpoint = 'sm',
}) => {
  // fallback value should not matter since we enforce through types that value is controlled when defaultValue is undefined
  const [internalValue, setInternalValue] = useState(defaultValue ?? '0');
  const id = useId();

  const { instanceId, getPanelId, getTabId } = useMemo(() => {
    const instId = id.replace(/:/g, '');
    return {
      instanceId: instId,
      getPanelId: (value: TabValue) => `${instanceId}-tabpanel-${value}`,
      getTabId: (value: TabValue) => `${instanceId}-tab-${value}`,
    };
  }, [id]);

  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleValueChange = useCallback(
    (newValue: TabValue) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [controlledValue, onValueChange],
  );

  const contextValue = useMemo(
    () => ({
      value,
      onValueChange: handleValueChange,
      iconBreakpoint,
      instanceId,
      getPanelId,
      getTabId,
    }),
    [
      value,
      handleValueChange,
      iconBreakpoint,
      instanceId,
      getPanelId,
      getTabId,
    ],
  );

  return (
    <WbTabsInternalContext.Provider value={contextValue}>
      {children}
    </WbTabsInternalContext.Provider>
  );
};
